import { CommonModule } from '@angular/common';
import { Component, OnInit, WritableSignal, signal, input, inject, InputSignal, model, computed } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { CartService } from '../../cart.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { SubscriptionsService } from '../../../settings/account/subscriptions/subscriptions.service';
import { tap } from 'rxjs';
import { NotificationService } from '../../notification/notification.service';
import { Subscription, UpdateSubscriptionPayload } from '../../../settings/account/subscriptions/subscriptions.types';
import { OnlyNumberDirective } from '../../directives/only-number.directive';
import { getProductSubscriptioninfo, openModalSkipProduct, openModalUnskipProduct } from '../../common/subscriptions.utils';
import { ModalContentService } from '../../modal-content/modal-content.service';

@Component({
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    OnlyNumberDirective
  ],
  providers: [
    SubscriptionsService
  ]
})

export class ProductCartPreview implements OnInit {
  signalsStoreService = inject(SignalsStoreService)
  cartService = inject(CartService)
  orderService = inject(OrderService)
  #router = inject(Router);
  #subscriptionsService = inject(SubscriptionsService);
  #notificationService = inject(NotificationService);
  #modalContentService = inject(ModalContentService);
  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity = model(1);

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      this.isMoreThanTen.set(product.quantity >= 10);
      return product;
    }
  })

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  isJustAddedProductNotification: InputSignal<boolean> = input(false, {
    alias: 'isJustAddedProductNotification'
  });

  isInOrder = input(false);

  isSkipped = input(false);

  isSubscription = input(false);

  showUpdateButton = computed(() => this.isMoreThanTen() && (this.previousValue !== +(this.newQuantity() || 0)))

  isMoreThanTen = signal(false);

  ngOnInit(): void {
    this.isInputValid = true;
  }

  removeCartProduct() {
    this.orderService.removeFirebaseProduct(this.productKey(), this.product(), this.signalsStoreService.firebaseOrder());
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  goToCustomizeBox() {
    this.signalsStoreService.showCartPreview.set(false);
    this.#router.navigate([`/shop/custom-box/${this.productKey()}/${this.product().id}`]);
  }

  unskip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalUnskipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      orderService: this.orderService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  skip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalSkipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      orderService: this.orderService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  #getSubscriptionInfoByProduct(product: any): Subscription | null {
    const subscriptions = this.signalsStoreService.subscriptions();
    return getProductSubscriptioninfo(subscriptions, product.id);
  }

  onQuantityChange() {
    if (!this.newQuantity())
      return;

    if (this.newQuantity() === 10)
      return this.isMoreThanTen.set(true);

    this.#updateFirebaseQuantity();
  }

  validateQuantity() {
    if (!this.newQuantity())
      return this.#notificationService.show({ text: 'Please enter a valid quantity', type: 'error' });

    this.#updateFirebaseQuantity();
  }

  #updateFirebaseQuantity() {
    this.orderService
      .updateFirebaseProduct(
        this.productKey(),
        {
          ...this.product(),
          quantity: +this.newQuantity()
        },
        this.signalsStoreService.firebaseOrder()
      );
  }
}
